import { useId, type FormEventHandler, type ReactNode } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { Size2MixRightSideModal } from '@shared/size2mix-shell';

interface EntityFormPanelProps {
    children: ReactNode;
    iconSrc?: string;
    isCreation: boolean;
    isDirty: boolean;
    isPending: boolean;
    isValid: boolean;
    metadata: string;
    onClose: () => void;
    onSubmit: FormEventHandler<HTMLFormElement>;
    title: string;
}

export function EntityFormPanel({
    children,
    iconSrc,
    isCreation,
    isDirty,
    isPending,
    isValid,
    metadata,
    onClose,
    onSubmit,
    title,
}: EntityFormPanelProps) {
    const formId = useId();

    return (
        <Size2MixRightSideModal
            footer={
                <>
                    <NJButton label="Annuler" onClick={onClose} type="button" variant="secondary" />
                    <NJButton
                        disabled={!isDirty || !isValid || isPending}
                        form={formId}
                        label={isCreation ? 'Créer' : 'Valider'}
                        type="submit"
                        variant="primary"
                    />
                </>
            }
            isOpen
            onClose={onClose}
            testId="production-entity-form"
            title={title}
            width="min(536px, calc(100vw - var(--nj-semantic-size-spacing-48)))"
        >
            {iconSrc && (
                <style>
                    {`
                        [data-testid="production-entity-form"] > div:first-child > p {
                            min-height: 32px;
                            display: flex;
                            align-items: center;
                            gap: var(--nj-semantic-size-spacing-12);
                        }
                        [data-testid="production-entity-form"] > div:first-child > p::before {
                            content: "";
                            width: 32px;
                            height: 32px;
                            flex: 0 0 32px;
                            background-image: url("${iconSrc}");
                            background-repeat: no-repeat;
                            background-position: center;
                            background-size: contain;
                        }
                    `}
                </style>
            )}
            <form
                id={formId}
                onSubmit={onSubmit}
                style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}
            >
                <div
                    style={{
                        flex: '1 1 0',
                        overflowY: 'auto',
                        padding: 'var(--nj-semantic-size-spacing-24) var(--nj-semantic-size-spacing-12)',
                        minHeight: 0,
                    }}
                >
                    <NJText
                        scale="sm"
                        style={{
                            display: 'block',
                            marginBottom: 'var(--nj-semantic-size-spacing-32)',
                            color: 'var(--nj-semantic-color-text-neutral-secondary-default)',
                        }}
                    >
                        Tous les champs avec * sont obligatoires
                    </NJText>
                    {children}
                </div>

                <NJText
                    as="p"
                    scale="xs"
                    style={{
                        margin: 0,
                        padding: 'var(--nj-semantic-size-spacing-8) var(--nj-semantic-size-spacing-16)',
                        borderTop: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                        color: 'var(--nj-semantic-color-text-neutral-secondary-default)',
                        fontStyle: 'italic',
                        flexShrink: 0,
                    }}
                >
                    {metadata}
                </NJText>
            </form>
        </Size2MixRightSideModal>
    );
}
